import { useIsInModal } from "@/flokit/compound/modals/is-in-modal-context";
import { cx } from "class-variance-authority";
import { useRef } from "react";
import { FloSelectBase, FloSelectOption, FloSelectProps } from ".";

import InputLabel from "../input-general/input-label";
import {
  getCorrectHtmlAttributesForInput,
  normalizeLabelProp,
} from "../input-general/label-prop";
import ValidationMessage, {
  ValidationProps,
} from "../input-general/validation";
import { Nullable } from "../types/Nullable";
import { useHtmlId } from "../utils/useHtmlId";
import { When } from "../when";
import { FormItemDecorationProps, RefDivWrapper } from "./common";

interface FloSingleSelectWithoutLabelProps
  extends Omit<FloSelectProps, "value" | "onChange" | "isMulti"> {
  value: Nullable<FloSelectOption>;
  onChange: (value: Nullable<FloSelectOption>) => void;
  isInModal?: boolean;
}
export type FloSingleSelectProps = FloSingleSelectWithoutLabelProps &
  FormItemDecorationProps &
  ValidationProps;

/**
 * @deprecated Use Flokit select component instead
 */
export const FloSingleSelect = (props: FloSingleSelectProps) => {
  const id = useHtmlId();
  const labelMethod = normalizeLabelProp(props.label);
  const {
    required,
    tooltip,
    disabled,
    isInModal: callerProvidedIsInModal,
    ...rest
  } = props;

  const portalRef = useRef<HTMLDivElement>();
  const isInModalContext = useIsInModal();
  const isInModal = callerProvidedIsInModal ?? isInModalContext;

  return (
    <div className={cx("flex w-full flex-col gap-y-2")}>
      <RefDivWrapper
        ref={isInModal ? portalRef : undefined}
        className={cx("flex flex-col gap-y-2")}
      >
        <InputLabel
          id={id}
          labelMethod={labelMethod}
          required={required}
          tooltip={tooltip}
          isElementInteractive={false}
        />
        {/**
         * TODO: Figure out what the correct accesibility treatment is for this
         * Will be covered by: 10285
         */}
        <When condition={!!props.subText}>
          <p className={cx("break-normal text-sm text-medium-hippo")}>
            {props.subText}
          </p>
        </When>
        <FloSelectBase
          {...rest}
          {...getCorrectHtmlAttributesForInput({
            labelProp: props.label,
            htmlId: id,
            elementIsNonInteractive: true,
            fieldIsRequired: required,
          })}
          disabled={disabled}
          required={required}
          hasError={props.shouldShowValidation}
          menuPortalTarget={isInModal ? portalRef.current : undefined}
          UNSAFE_Components={props.UNSAFE_Components}
        />
      </RefDivWrapper>
      <ValidationMessage
        shouldShowValidation={props.shouldShowValidation}
        validationMessage={props.validationMessage}
      />
    </div>
  );
};
